import { Popup } from 'semantic-ui-react';
import { DealStates } from '~/constants';
import './DealLifecycleStepper.scss';

interface DealLifecycleStepperProps {
  dealState: number;
  isDeclined?: boolean;
  isInvestor?: boolean;
}

const INVESTOR_START_INDEX = 2;

const STOPS = [
  { label: 'Draft', description: 'The developer is still preparing the project. It has not yet been submitted to Conductor.', visibility: 'Visible to your team and Conductor.' },
  { label: 'In Review', description: 'Conductor is reviewing the project before releasing it to investors.', visibility: 'Visible to your team and Conductor.' },
  { label: 'Bidding', description: 'The project is open to investors. Bids are being collected and reviewed.', visibility: 'Visible to assigned parties.' },
  { label: 'Diligence', description: 'A bid has been accepted. The investor and developer are working through diligence and definitive documents.', visibility: 'Previous bidders see a snapshot; the active bidder retains full visibility.' },
  { label: 'Executed', description: 'Definitive documents have been signed.', visibility: '' }
];

function dealStateToStepIndex(dealState: number): number {
  if (dealState <= DealStates.READY_TO_SUBMIT) return 0;
  if (dealState === DealStates.IN_REVIEW || dealState === DealStates.MATCHING) return 1;
  if (
    dealState === DealStates.AWAITING_BID ||
    dealState === DealStates.PENDING_BID ||
    dealState === DealStates.DECLINED
  ) return 2;
  if (dealState === DealStates.IN_DILIGENCE) return 3;
  if (dealState === DealStates.EXECUTED || dealState === DealStates.COMPLETED) return 4;
  return 0;
}

const VIEW_W = 600;
const VIEW_H = 70;
const PAD_X = 36;
const TRACK_Y = 18;
const RADIUS = 10;
const LINE_THICKNESS = 4;

export default function DealLifecycleStepper({ dealState, isDeclined = false, isInvestor = false }: DealLifecycleStepperProps) {
  const declined = isDeclined;
  const offset = isInvestor ? INVESTOR_START_INDEX : 0;
  const stops = STOPS.slice(offset);
  const rawIndex = declined ? -1 : dealStateToStepIndex(dealState);
  const currentIndex = declined ? -1 : Math.max(0, rawIndex - offset);

  const step = (VIEW_W - PAD_X * 2) / (stops.length - 1);
  const xs = stops.map((_, i) => PAD_X + step * i);

  return (
    <div className="deal-lifecycle-stepper">
      {declined && (
        <div className="deal-lifecycle-stepper__declined-pill">Declined</div>
      )}
      <svg
        className="deal-lifecycle-stepper__svg"
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Deal lifecycle progress"
      >
        {xs.slice(0, -1).map((x, i) => {
          const x2 = xs[i + 1];
          const reached = !declined && i < currentIndex;
          return (
            <line
              key={`seg-${i}`}
              x1={x + RADIUS}
              x2={x2 - RADIUS}
              y1={TRACK_Y}
              y2={TRACK_Y}
              strokeWidth={LINE_THICKNESS}
              className={reached ? 'deal-lifecycle-stepper__segment--reached' : 'deal-lifecycle-stepper__segment'}
            />
          );
        })}
        {stops.map((stop, i) => {
          const reached = !declined && i <= currentIndex;
          const hoverHalfWidth = step / 2;
          return (
            <Popup
              key={`stop-${i}`}
              position="top center"
              size="small"
              trigger={
                <g className="deal-lifecycle-stepper__stop">
                  <circle
                    cx={xs[i]}
                    cy={TRACK_Y}
                    r={RADIUS}
                    className={reached ? 'deal-lifecycle-stepper__circle--reached' : 'deal-lifecycle-stepper__circle'}
                  />
                  <text
                    x={xs[i]}
                    y={TRACK_Y + RADIUS + 22}
                    textAnchor="middle"
                    className={i === currentIndex ? 'deal-lifecycle-stepper__label--current' : 'deal-lifecycle-stepper__label'}
                  >
                    {stop.label}
                  </text>
                  <rect
                    x={xs[i] - hoverHalfWidth}
                    y={0}
                    width={hoverHalfWidth * 2}
                    height={VIEW_H}
                    className="deal-lifecycle-stepper__hover-target"
                  />
                </g>
              }
              content={
                <div>
                  <div className="deal-lifecycle-stepper__popup-title">{stop.label}</div>
                  <div className="deal-lifecycle-stepper__popup-body">{stop.description}</div>
                  {!isInvestor && stop.visibility && (
                    <div className="deal-lifecycle-stepper__popup-visibility">{stop.visibility}</div>
                  )}
                </div>
              }
            />
          );
        })}
      </svg>
    </div>
  );
}
